import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import en from './locales/en.json';
import ru from './locales/ru.json';
import uk from './locales/uk.json';
import de from './locales/de.json';

const locales = { en, ru, uk, de };

const SUPPORTED_LANGUAGES = ['en', 'ru', 'uk', 'de'];
const STORAGE_KEY = 'void_oracle_lang';

// Ukrainian timezones (IANA)
const UA_TIMEZONES = ['Europe/Kiev', 'Europe/Kyiv', 'Europe/Zaporozhye', 'Europe/Uzhgorod', 'Europe/Simferopol'];

// Detect if user is likely in Ukraine via timezone
const isLikelyUkraine = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return UA_TIMEZONES.includes(tz);
  } catch {
    return false;
  }
};

// Detect browser language
const detectBrowserLanguage = () => {
  const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
  if (SUPPORTED_LANGUAGES.includes(browserLang)) {
    return browserLang;
  }
  return null; // No match — let geo decide
};

// Get initial language with geo-aware fallback
const getInitialLanguage = () => {
  // 1. Check localStorage (user's saved preference — highest priority)
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
    return saved;
  }
  // 2. Browser language (if matches our supported list)
  const browserLang = detectBrowserLanguage();
  if (browserLang) {
    return browserLang;
  }
  // 3. Geo fallback: Ukraine → uk, everywhere else → en
  return isLikelyUkraine() ? 'uk' : 'en';
};

// Language Context
const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = (lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
      localStorage.setItem(STORAGE_KEY, lang);
    }
  };

  // Get nested translation value
  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = locales[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        // Fallback to English
        value = locales.en;
        for (const fallbackKey of keys) {
          value = value?.[fallbackKey];
        }
        break;
      }
    }
    
    if (typeof value === 'string') {
      // Replace {param} with actual values
      Object.entries(params).forEach(([param, val]) => {
        value = value.replace(`{${param}}`, val);
      });
    }
    
    return value || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Hook to set dynamic page titles
export const usePageTitle = (pageKey) => {
  const { t } = useLanguage();
  
  useEffect(() => {
    const title = t(`pages.${pageKey}`);
    if (title && title !== `pages.${pageKey}`) {
      document.title = title;
    }
  }, [pageKey, t]);
};

// Language names for display
export const LANGUAGE_NAMES = {
  en: { name: 'English', flag: '🇬🇧' },
  ru: { name: 'Русский', flag: '🇷🇺' },
  uk: { name: 'Українська', flag: '🇺🇦' },
  de: { name: 'Deutsch', flag: '🇩🇪' }
};
